import { FC, useCallback, useEffect, useMemo } from 'react';
import { ChartWidget } from '../chart/chart.widget';
import { Actions, Layout, Model, TabNode } from 'flexlayout-react';
import { useLayoutContext } from './layout-context';
import {
  clearWorkspaceFullscreenScope,
  getWorkspaceFullscreenExit$,
  setWorkspaceFullscreenScope
} from './workspace-fullscreen.service';

type TimeframeLayoutProps = {};

type Timeframe = { timeframe: string };
type TabJson = {
  type: 'tab';
  id: string;
  name: string;
  config: { timeframeIndex: number };
};
type RowJson = {
  type: 'row';
  weight: number;
  children: Array<
    RowJson | { type: 'tabset'; weight: number; children: TabJson[] }
  >;
};

const MAX_TABS = 6;

const toTabs = (symbolId: string, timeframes: Timeframe[]): TabJson[] =>
  timeframes.slice(0, MAX_TABS).map(({ timeframe }, index) => ({
    type: 'tab',
    id: symbolId + timeframe,
    name: timeframe,
    config: { timeframeIndex: index }
  }));

const tabset = (childTab: TabJson, weight = 50) => ({
  type: 'tabset' as const,
  weight,
  children: [childTab]
});

const buildGrid = (tabs: TabJson[]): RowJson['children'] => {
  if (tabs.length === 0) return [];
  if (tabs.length === 1) return [tabset(tabs[0], 100)];

  const rows: TabJson[][] = [];
  for (let index = 0; index < tabs.length; index += 2) {
    rows.push(tabs.slice(index, index + 2));
  }

  const rowWeight = 100 / rows.length;

  return rows.map((row) => ({
    type: 'row',
    weight: rowWeight,
    children:
      row.length === 1
        ? [tabset(row[0], 100)]
        : [tabset(row[0], 50), tabset(row[1], 50)]
  }));
};

const buildModel = (
  symbolId: string,
  symbol: string | undefined,
  tabs: TabJson[]
) =>
  Model.fromJson({
    global: {
      tabEnableClose: false,
      splitterSize: 1,
      tabEnableDrag: true,
      rootOrientationVertical: true
    },
    borders: [],
    layout: {
      type: 'row',
      weight: 100,
      id: `${symbol}-timeframes-root`,
      children: buildGrid(tabs)
    }
  });

const collectTabIds = (node: any, acc: Set<string>) => {
  if (!node) return;
  if (node.type === 'tab' && node.id) {
    acc.add(node.id);
  }
  if (Array.isArray(node.children)) {
    node.children.forEach((child: any) => collectTabIds(child, acc));
  }
};

const hasSameTabs = (jsonLayout: any, expectedIds: Set<string>) => {
  const storedIds = new Set<string>();
  collectTabIds(jsonLayout, storedIds);

  if (storedIds.size !== expectedIds.size) return false;
  return (
    Array.from(expectedIds).every((id) => storedIds.has(id)) &&
    Array.from(storedIds).every((id) => expectedIds.has(id))
  );
};

const restoreModel = (
  storageKey: string,
  expectedIds: Set<string>
): Model | null => {
  if (typeof window === 'undefined') return null;

  const raw = localStorage.getItem(storageKey);
  if (!raw) return null;

  try {
    const json = JSON.parse(raw);
    if (!hasSameTabs(json.layout, expectedIds)) return null;

    const model = Model.fromJson(json);
    const allPresent = Array.from(expectedIds).every(
      (id) => !!model.getNodeById(id)
    );
    return allPresent ? model : null;
  } catch (e) {
    console.warn('Failed to restore timeframe layout', e);
    return null;
  }
};

const persistModel = (storageKey: string, model: Model) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(storageKey, JSON.stringify(model.toJson()));
  } catch (e) {
    console.warn('Failed to persist timeframe layout', e);
  }
};

export const TimeframeLayout: FC<TimeframeLayoutProps> = () => {
  const { state, chartId: symbolId } = useLayoutContext();

  const storageKey = `timeframe-layout-${symbolId}`;

  const timeframes = useMemo(
    () => (state?.timeframes ?? []).slice(0, MAX_TABS),
    [state]
  );

  const expectedIds = useMemo(
    () => new Set(timeframes.map(({ timeframe }) => symbolId + timeframe)),
    [symbolId, timeframes]
  );

  const timeframeModel = useMemo(() => {
    if (!state) return null;
    const tabs = toTabs(symbolId, timeframes);
    return (
      restoreModel(storageKey, expectedIds) ??
      buildModel(symbolId, state.marketConfig?.symbol, tabs)
    );
  }, [state, symbolId, timeframes, expectedIds, storageKey]);

  const factory = useCallback((node: TabNode) => {
    const { timeframeIndex } = node.getConfig();
    return <ChartWidget timeframeIndex={timeframeIndex} />;
  }, []);

  const handleModelChange = useCallback(
    (model: Model) => {
      persistModel(storageKey, model);
      setWorkspaceFullscreenScope(
        symbolId,
        Boolean(model.getMaximizedTabset())
      );
    },
    [storageKey, symbolId]
  );

  const classNameMapper = useCallback((defaultClassName: string) => {
    if (defaultClassName.includes('flexlayout__tabset_tabbar_outer')) {
      return `${defaultClassName} flexlayout__timeframe_tabbar_outer`;
    }

    return defaultClassName;
  }, []);

  useEffect(() => {
    if (!timeframeModel) return;

    setWorkspaceFullscreenScope(
      symbolId,
      Boolean(timeframeModel.getMaximizedTabset())
    );

    return () => {
      clearWorkspaceFullscreenScope(symbolId);
    };
  }, [symbolId, timeframeModel]);

  useEffect(() => {
    if (!timeframeModel) return;

    const subscription = getWorkspaceFullscreenExit$().subscribe(() => {
      const maximizedTabset = timeframeModel.getMaximizedTabset();
      if (!maximizedTabset) return;

      timeframeModel.doAction(Actions.maximizeToggle(maximizedTabset.getId()));
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [timeframeModel]);

  if (!timeframeModel) return null;

  return (
    <Layout
      model={timeframeModel}
      factory={factory}
      onModelChange={handleModelChange}
      classNameMapper={classNameMapper}
    />
  );
};
